import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';

export function Terms() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-[--bg-secondary] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="inline-flex items-center text-[--text-secondary] hover:text-[--text-primary] mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Link>

        <div className="bg-[--bg-primary] rounded-lg shadow-lg overflow-hidden">
          <div className="p-8">
            {/* --- START OF MODIFIED CONTENT --- */}

            <h1 className="text-3xl font-bold text-[--text-primary] mb-6">
              Scholar42 by InfiniPouch Platform Terms of Service (Beta)
            </h1>
            <p className="text-sm text-[--text-secondary] mb-4">
              {/* ⚠️ IMPORTANT: Update this date when the terms are finalized. */}
              Last Updated: [Enter Date]
            </p>
            <div className="prose dark:prose-invert max-w-none">

              <p>
                <strong>Legal Notice</strong> These Terms of Service (the "Terms") form a binding contract between you and <strong>InfiniPouch LLC</strong> when you create an account, purchase Credits, or otherwise use the Service. The Service is in <strong>Beta/trial-operation phase</strong>; functionality, performance, and availability may change or be interrupted without notice. If you do not accept these Terms, do not use the Service.
              </p>

              <h2>1. Definitions</h2>
              <table>
                <thead>
                  <tr>
                    <th>Term</th>
                    <th>Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>"InfiniPouch," "we," "our," "us"</strong></td>
                    <td><strong>InfiniPouch LLC</strong>, the company that owns and operates the Scholar42 platform.</td>
                  </tr>
                  <tr>
                    <td><strong>"Service"</strong></td>
                    <td>The Scholar42 website, applications, dashboards, APIs, and all related tooling, documentation, and support, also referred to as the <strong>"Scholar42 Platform"</strong>.</td>
                  </tr>
                  <tr>
                    <td><strong>"User," "you," "your"</strong></td>
                    <td>Any individual or entity that accesses or uses the Service</td>
                  </tr>
                  <tr>
                    <td><strong>"Credits"</strong></td>
                    <td>Pre-paid, non-refundable units redeemable within the Service for generating Files. Credits are personal, non-transferable, and have no cash value. They remain valid for five (5) years from purchase unless the Service winds down (§ 6.5).</td>
                  </tr>
                  <tr>
                    <td><strong>"User Content"</strong></td>
                    <td>Data, prompts, documents, or materials submitted to the Service by or on behalf of a User</td>
                  </tr>
                  <tr>
                    <td><strong>"File"</strong></td>
                    <td>Any output generated by the Service in response to User Content</td>
                  </tr>
                </tbody>
              </table>

              <h2>2. Acceptance of Terms</h2>
              <ul>
                <li>
                  <strong>Capacity.</strong> You confirm that you have the legal capacity to enter into these Terms. Users below the legal age in their jurisdiction must have parental or guardian consent (§ 16).
                </li>
                <li>
                  <strong>Beta Acknowledgement.</strong> You understand that the Service is in Beta, may contain errors, and is provided "as is" without performance guarantees or service-level agreements.
                </li>
                <li>
                  <strong>Agreement.</strong> By using the Service or purchasing Credits, you agree to be bound by these Terms.
                </li>
              </ul>

              <h2>3. Intellectual Property</h2>
              <h3>3.1 User Content</h3>
              <p>
                You retain full ownership and rights to your original User Content. <strong>InfiniPouch</strong> receives only a limited license to process User Content solely to deliver the Service. You represent and warrant you have all necessary rights to submit your content. For questions related to content rights, please contact <a href="mailto:support@scholar42.com">support@scholar42.com</a>.
              </p>
              <h3>3.2 Generated Files</h3>
              <p>
                Upon successful delivery and payment, all rights to the specific File output transfer to you. This excludes underlying algorithms, templates, proprietary formatting systems, or third-party content. You may not claim ownership over any structural or technical methods used to create the File.
              </p>
              <h3>3.3 Platform IP</h3>
              <p>
                All underlying models, algorithms, software components, UI/UX designs, branding elements, and proprietary methods remain the exclusive property of <strong>InfiniPouch</strong> or its licensors. Unauthorized use, distribution, or replication is strictly prohibited. To request permission for any use outside these Terms, please contact <a href="mailto:support@scholar42.com">support@scholar42.com</a>.
              </p>
              <h3>3.4 DMCA/IP Takedown</h3>
              <p>
                For intellectual property notices or infringement claims, contact us immediately at <a href="mailto:security@scholar42.com">security@scholar42.com</a>.
              </p>

              <h2>4. Academic Integrity and Responsibility</h2>
              <p>
                The <strong>Scholar42 Platform</strong> assists exclusively in language refinement, manuscript preparation, formatting, and citation management. The accuracy, originality, interpretation, ethical compliance, and scholarly contribution of all content remain entirely the User's responsibility. Users must independently verify all content prior to submission or publication and adhere to institutional and journal-specific guidelines regarding the disclosure of AI-assisted writing.
              </p>
              <p>
                Use of the Service to fabricate research, misrepresent authorship, bypass peer-review ethics, or engage in unethical practices is strictly prohibited. <strong>We</strong> disclaim all responsibility for misuse or unethical application of the Service.
              </p>

              <h2>5. Data Privacy and Confidentiality</h2>
              <h3>5.1 Data Usage and Temporary Storage</h3>
              <p>
                User Content is processed solely for generating Files. <strong>InfiniPouch</strong> does not retain or use User Content for secondary purposes. Generated Files are temporarily stored in <strong>Supabase Storage</strong> to enable user access. Users are strongly encouraged to download and manage Files promptly and delete them if no longer needed. We do not guarantee permanent storage and are not responsible for data loss. For questions about data privacy, please contact <a href="mailto:privacy@scholar42.com">privacy@scholar42.com</a>.
              </p>
              <h3>5.2 User-Controlled Deletion</h3>
              <p>
                Files generated by the Service can be deleted at any time by the User via their dashboard. We do not automatically delete content unless explicitly instructed by the User.
              </p>
              <h3>5.3 No Marketing Use</h3>
              <p>
                <strong>InfiniPouch</strong> does not disclose User Content, Files, or personal identifiers to third parties for marketing, advertising, or unrelated analytics purposes.
              </p>

              <h2>6. Payment, Credits, and Billing</h2>
              <h3>6.1 Credit Purchase</h3>
              <p>
                All prices are in USD and exclude applicable taxes. Users are responsible for local tax compliance unless otherwise stated.
              </p>
              <h3>6.2 Credit Use</h3>
              <p>
                Before generating a File, you will see the required Credit cost. Submitting a request authorizes <strong>us</strong> to deduct the relevant amount from your balance.
              </p>
              <h3>6.3 Credit Terms</h3>
              <p>
                Credits have no cash value and may not be transferred, resold, or refunded except as outlined in § 6.5.
              </p>
              <h3>6.4 Invoicing</h3>
              <p>
                A PDF invoice will be emailed to your registered account after each successful purchase. No physical invoices are provided.
              </p>
              <h3>6.5 Wind-Down Refunds</h3>
              <p>
                If <strong>InfiniPouch</strong> discontinues the Service, we will provide 30 days' notice and allow refund requests for unused, paid Credits. Refunds are issued to the original payment method. Unclaimed refunds may be subject to applicable unclaimed-property laws.
              </p>

              <h2>7. Refund Policy</h2>
              <p>
                All Credit purchases and redemptions are final, except as described in § 6.5. If a File fails to generate due to a system error, you may notify us within seven (7) days for review. We may re-issue Credits at our sole discretion. To submit refund inquiries, please contact <a href="mailto:billing@scholar42.com">billing@scholar42.com</a>.
              </p>

              <h2>8. Prohibited Use and Conduct</h2>
              <ul>
                <li>Reverse-engineer, scrape, bulk export data from, or interfere with the Service</li>
                <li>Upload unlawful, harmful, infringing, fraudulent, deceptive, defamatory, discriminatory, or unethical materials</li>
                <li>Generate misleading, politically manipulative, religiously inflammatory, hateful, or abusive content</li>
                <li>Violate international sanctions, export control laws, or local regulations</li>
              </ul>
              <p>
                We reserve the right to suspend or terminate accounts for violations, inappropriate usage, or abuse of the Service.
              </p>

              <h2>9. Security</h2>
              <p>
                We apply TLS encryption for data transmission. Files are stored in Supabase Storage using AES-256 encryption at rest. Access is restricted to authorized processes and authenticated users. Users must maintain their own account security and content backups. To report security vulnerabilities, contact <a href="mailto:security@scholar42.com">security@scholar42.com</a>.
              </p>

              <h2>10. Third-Party Disclaimer</h2>
              <p>
                <strong>InfiniPouch</strong> is not liable for disruptions, security incidents, data loss, or performance issues arising from third-party service providers (including but not limited to Supabase, cloud infrastructure providers, payment gateways).
              </p>

              <h2>11. Warranty & Limitation of Liability</h2>
              <p>
                The Service is provided "as is." <strong>InfiniPouch</strong> explicitly disclaims all warranties, including suitability for specific purposes. <strong>InfiniPouch</strong> is not liable for indirect, incidental, special, or consequential damages. <strong>InfiniPouch's</strong> total liability shall not exceed the amount paid by you within the twelve (12) months prior to any claim.
              </p>
              <p>
                <strong>We</strong> bear no responsibility for how Users interpret, apply, publish, or otherwise use Files. Users assume full responsibility for any outcomes arising from their use of the Service.
              </p>

              <h2>12. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless <strong>InfiniPouch</strong> from claims, liabilities, damages, losses, and expenses (including reasonable attorney fees) arising from your violation of these Terms or improper use of the Service. For legal matters, contact <a href="mailto:legal@scholar42.com">legal@scholar42.com</a>.
              </p>

              <h2>13. Force Majeure</h2>
              <p>
                <strong>InfiniPouch</strong> shall not be liable for failures resulting from circumstances beyond our reasonable control.
              </p>

              <h2>14. Governing Law & Jurisdiction</h2>
              <p>
                {/* ⚠️ CRITICAL LEGAL REVIEW REQUIRED: This section MUST be updated to reflect the correct jurisdiction for InfiniPouch LLC. The original "Singapore" is likely incorrect. Consult your lawyer. */}
                These Terms shall be governed exclusively by the laws of <strong>[State/Country, e.g., the State of Delaware, USA]</strong>. Any disputes shall be subject to the exclusive jurisdiction of courts in <strong>[City, State/Country, e.g., Wilmington, Delaware]</strong>.
              </p>

              <h2>15. Export Controls / Sanctions</h2>
              <p>
                You warrant compliance with applicable export control and sanctions laws and confirm you are not accessing from sanctioned territories.
              </p>

              <h2>16. Parental Consent</h2>
              <p>
                If you are under the legal age in your jurisdiction, you must obtain consent from a parent or guardian to use the Service.
              </p>

              <h2>17. Notices & Communication</h2>
              <p>
                All official notices are effective upon email delivery to your registered account or via in-app notification. Notices to <strong>us</strong> should be sent to the email addresses listed in this document's contact table.
              </p>
              <table>
                <thead>
                  <tr>
                    <th>Inquiry Type</th>
                    <th>Contact Email</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>General Support, Content Rights, Parental Consent</td>
                    <td><a href="mailto:support@scholar42.com">support@scholar42.com</a></td>
                  </tr>
                  <tr>
                    <td>Billing & Refunds</td>
                    <td><a href="mailto:billing@scholar42.com">billing@scholar42.com</a></td>
                  </tr>
                  <tr>
                    <td>Security & IP/DMCA Takedown</td>
                    <td><a href="mailto:security@scholar42.com">security@scholar42.com</a></td>
                  </tr>
                  <tr>
                    <td>Privacy & Data Protection</td>
                    <td><a href="mailto:privacy@scholar42.com">privacy@scholar42.com</a></td>
                  </tr>
                  <tr>
                    <td>Legal & Compliance</td>
                    <td><a href="mailto:legal@scholar42.com">legal@scholar42.com</a></td>
                  </tr>
                </tbody>
              </table>

              <h2>18. Severability & Entire Agreement</h2>
              <p>
                If any clause is found invalid, the remainder of the Terms remains fully enforceable. These Terms constitute the entire agreement between <strong>InfiniPouch</strong> and you.
              </p>

              <h2>19. Survival</h2>
              <p>
                Sections relating to IP, indemnification, disclaimers, liability limits, and jurisdiction survive termination.
              </p>

              <h2>20. Modifications</h2>
              <p>
                We may modify these Terms upon 30 days' notice for material changes. Continued use constitutes acceptance of updated Terms.
              </p>

              {/* --- END OF MODIFIED CONTENT --- */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}